import React, { useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import {
  TextField,
  Button,
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  FormHelperText,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { omit, toUpper } from 'lodash'; // For capitalizing the first letter
import LayoutContainer from '../components/LayoutContainer';
import AdminHeader from '../components/AdminHeader';
import authService from '../services/authService';
import { toast } from 'react-toastify';
import { Visibility, VisibilityOff } from '@mui/icons-material';

// Validation Schema using Yup
const validationSchema = Yup.object({
  old_password: Yup.string().required('Current password is required'),
  new_password: Yup.string()
    .min(6, 'Password must be at least 6 characters')
    .required('New password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('new_password'), null], 'Passwords must match')
    .required('Confirm password is required'),
});

const ChangePassword = () => {
  const roles =
    localStorage?.getItem('roles') &&
    JSON?.parse(localStorage?.getItem('roles'));
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleClickShowPassword = () => {
    setShowCurrentPassword((prev) => !prev);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleSubmit = async (values) => {
    try {
      const res = await authService?.changePassword(
        omit(values, 'confirmPassword')
      );

      if (res?.status === 200) {
        toast?.success('Password changed successfully');
      } else {
        toast?.error('Failed to change password');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      toast?.error('An error occurred while changing the password');
    }
  };

  return (
    <LayoutContainer>
      <AdminHeader
        title={'CHANGE PASSWORD'}
        backTo={
          roles?.includes('STATION MANAGER') ? '/stationUpdate' : '/events'
        }
      />
      <Box
        className='page-content-wrapper scrollable'
        p={3}
        sx={{
          display: 'flex',
          backgroundColor: '#efefef',
          //   justifyContent: 'center',
        }}
      >
        <Formik
          initialValues={{
            old_password: '',
            new_password: '',
            confirmPassword: '',
            phone: localStorage?.getItem('phone'),
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ handleChange, handleBlur, values, errors, touched }) => (
            <Form style={{ width: '100%', maxWidth: '600px' }}>
              <Card>
                <CardContent>
                  <Grid container spacing={3} sx={{ p: 1 }}>
                    {/* Current Password */}
                    <Grid item xs={12}>
                      <Typography variant='body2' sx={{ fontWeight: 600 }}>
                        CURRENT PASSWORD <span style={{ color: 'red' }}>*</span>
                      </Typography>
                      <TextField
                        name='old_password'
                        type={showCurrentPassword ? 'text' : 'password'}
                        fullWidth
                        value={values.old_password}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        sx={{
                          backgroundColor: '#f5f5f5',
                          borderRadius: 1,
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor:
                                touched.old_password && errors.old_password
                                  ? '#d32f2f'
                                  : '#ccc',
                            },
                          },
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position='end'>
                              <IconButton
                                onClick={handleClickShowPassword}
                                onMouseDown={handleMouseDownPassword}
                                edge='end'
                              >
                                {showCurrentPassword ? (
                                  <VisibilityOff />
                                ) : (
                                  <Visibility />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />

                      {/* <TextField
                        name='old_password'
                        type='password'
                        fullWidth
                        value={values.old_password}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        sx={{
                          backgroundColor: '#f5f5f5',
                          borderRadius: 1,
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor:
                                touched.old_password && errors.old_password
                                  ? '#d32f2f'
                                  : '#ccc',
                            },
                          },
                        }}
                      /> */}
                      {touched.old_password && errors.old_password && (
                        <FormHelperText error>
                          {errors.old_password}
                        </FormHelperText>
                      )}
                    </Grid>

                    {/* New Password */}
                    <Grid item xs={12}>
                      <Typography variant='body2' sx={{ fontWeight: 600 }}>
                        NEW PASSWORD <span style={{ color: 'red' }}>*</span>
                      </Typography>
                      <TextField
                        name='new_password'
                        type={showNewPassword ? 'text' : 'password'}
                        fullWidth
                        value={values.new_password}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        sx={{
                          backgroundColor: '#f5f5f5',
                          borderRadius: 1,
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor:
                                touched.new_password && errors.new_password
                                  ? '#d32f2f'
                                  : '#ccc',
                            },
                          },
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position='end'>
                              <IconButton
                                onClick={() =>
                                  setShowNewPassword(!showNewPassword)
                                }
                                edge='end'
                              >
                                {showNewPassword ? (
                                  <VisibilityOff />
                                ) : (
                                  <Visibility />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />

                      {touched.new_password && errors.new_password && (
                        <FormHelperText error>
                          {errors.new_password}
                        </FormHelperText>
                      )}
                    </Grid>

                    {/* Confirm New Password */}
                    <Grid item xs={12}>
                      <Typography variant='body2' sx={{ fontWeight: 600 }}>
                        CONFIRM NEW PASSWORD{' '}
                        <span style={{ color: 'red' }}>*</span>
                      </Typography>
                      <TextField
                        name='confirmPassword'
                        type={showConfirmPassword ? 'text' : 'password'}
                        fullWidth
                        value={values.confirmPassword}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        sx={{
                          backgroundColor: '#f5f5f5',
                          borderRadius: 1,
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor:
                                touched.confirmPassword &&
                                errors.confirmPassword
                                  ? '#d32f2f'
                                  : '#ccc',
                            },
                          },
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position='end'>
                              <IconButton
                                onClick={() =>
                                  setShowConfirmPassword(!showConfirmPassword)
                                }
                                edge='end'
                              >
                                {showConfirmPassword ? (
                                  <VisibilityOff />
                                ) : (
                                  <Visibility />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />

                      {touched.confirmPassword && errors.confirmPassword && (
                        <FormHelperText error>
                          {errors.confirmPassword}
                        </FormHelperText>
                      )}
                    </Grid>
                  </Grid>

                  {/* Submit Button */}
                  <Box
                    sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}
                  >
                    <Button
                      type='submit'
                      variant='contained'
                      sx={{
                        backgroundColor: '#be210b',
                        textTransform: 'none',
                        padding: '10px 20px',
                        borderRadius: '4px',
                        fontWeight: 600,
                      }}
                    >
                      SUBMIT
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Form>
          )}
        </Formik>
      </Box>
    </LayoutContainer>
  );
};

export default ChangePassword;
